import joi from 'joi';

import type { LabelFilter } from './labelFilterValue';
import { asScalarFilterTupleValue } from './labelFilterValue';

export type FilterValue = boolean | string | number | null;
const filterValueSchemas = [
  joi.boolean(),
  joi.string(),
  joi.number(),
  joi.allow(null),
];

const labelFilterSchema = joi
  .object({
    name: joi.string().optional(),
    value: joi.string().optional(),
  })
  .or('name', 'value');

export type Filter =
  | [string, ...(FilterValue | FilterValue[] | LabelFilter[])[]]
  | AttributeFilter;

// used for dynamic attribute filters [<attributeName>, <filterOperator>, <filterValue>]
export type AttributeFilter = [string, string, FilterValue];

export const filterSchema = joi
  .array()
  .ordered(joi.string().required())
  .items(
    ...filterValueSchemas,
    joi.array().items(...filterValueSchemas),
    joi.array().items(labelFilterSchema).min(1),
  );

/**
 * Filters can be plain object of [column]: filterRule, or can have also (AND with filters) or (OR with filter).
 *
 * The type is actually something like
 * ```
 * {
 *   [column: string EXCEPT and, or]: Filter;
 *   and?: Filters[];
 *   or?: Filters[];
 * }
 * ```
 * However, typescript does not allow to declare that. This type definition
 * is the most exact approximation we were able to implement.
 *
 * f = {and: andFilter, or: orFilter, ...columnsFilter}
 * is equivalent to f = {and: [andFilter, orFilter, columnsFilter]}
 */
export type Filters =
  | {
      // Filters[] | undefined is there just to satisfy `and` and `or` keys.
      // Under column key, there is always Filter.
      [column: string]: Filter | Filters[] | undefined;
      and?: Filters[];
      or?: Filters[];
    }
  | ({
      [column: string]: Filter;
    } & {
      and?: never;
      or?: never;
    });

export const isFilter = (
  filter: Filter | Filters[] | undefined,
): filter is Filter => Array.isArray(filter) && typeof filter[0] === 'string';

export const filtersSchema = joi
  .object()
  .keys({
    and: joi.array().items(joi.link('#Filters')),
    or: joi.array().items(joi.link('#Filters')),
  })
  .pattern(joi.string(), filterSchema)
  .id('Filters');

export const getFilterTupleOperator = (
  filter: Filter,
): FilterValue | FilterValue[] => {
  if (filter[0] === 'color') {
    return 'color';
  }

  const operator = asScalarFilterTupleValue(filter[filter.length - 2]);
  if (operator !== undefined) {
    return operator;
  }

  return filter[0] as FilterValue;
};

export const getFilterTupleOperand = (
  filter: Filter,
  offsetFromEnd: number,
): FilterValue | FilterValue[] | undefined =>
  asScalarFilterTupleValue(filter[filter.length - offsetFromEnd]);
